import { useMutation, useQueryClient } from "react-query";
import { BACKEND_DOMAIN } from "utils/constants";
import { buildRequestHeaders, showMutationAlert } from "utils/helpers";
import { request } from "graphql-request";
import { useAccessToken } from "hooks/useAccessToken";
import { graphql } from "gql/gql";
import useAlert from "hooks/useAlert";
import { buildingPoliciesQueryKey } from "ApiLayer/Policies/queryBuildingPolicies";
import { buildingFootprintsQueryKey } from "queries/useBuildingFootprintsQuery";
import { buildingModelConstructionQueryKey } from "queries/Construction/useBuildingModelConstruction";
import { useResetRecommendedBMCRPQueries } from "hooks/useResetRecommendedBMCRPQueries";
import { buildingQueryKey } from "ApiLayer/Building/queryBuildingModel";

interface MutationFnVariables {
    buildingProperties: BuildingProperties;
    buildingUid?: string | null;
    buildingFootprint?: any;
    carbonPolicyIds?: number[];
}

export const useUpdateBuildingModelMutation = (buildingModelUid: string, onSuccess: () => void, onError: () => void, buildingAddress?: Address | undefined) => {
    const accessToken = useAccessToken();
    const alert = useAlert();
    const queryClient = useQueryClient();
    const resetRecommendedBMCRPQueries = useResetRecommendedBMCRPQueries(buildingModelUid);

    return useMutation({
        onSuccess: () => {
            onSuccess();
            queryClient.invalidateQueries(buildingQueryKey);

            queryClient.invalidateQueries({
                queryKey: [...buildingPoliciesQueryKey, buildingModelUid],
            });
            queryClient.invalidateQueries({
                queryKey: [buildingModelConstructionQueryKey, buildingModelUid],
            });
            if (buildingAddress) {
                queryClient.invalidateQueries({
                    queryKey: [...buildingFootprintsQueryKey, buildingAddress],
                });
            }
            alert.success("Construction data updated.");
            resetRecommendedBMCRPQueries();
        },
        onError: (errors) => {
            onError();
            showMutationAlert(errors, alert);
        },
        mutationFn: async ({ buildingProperties, buildingUid, buildingFootprint, carbonPolicyIds }: MutationFnVariables) =>
            mutationFn(accessToken, buildingModelUid, buildingProperties, buildingUid, buildingFootprint, carbonPolicyIds),
    });
};

const mutationFn = (accessToken: string, buildingModelUid: string, buildingProperties: BuildingProperties, buildingUid?: string | null, buildingFootprint?: any, carbonPolicyIds?: number[]) =>
    request({
        url: `${BACKEND_DOMAIN}/graphql`,
        document: mutation,
        variables: {
            buildingModelUid,
            buildingProperties: {
                ...buildingProperties,
                buildingUid,
                buildingFootprint,
            },
            carbonPolicyIds,
        },
        requestHeaders: buildRequestHeaders(accessToken),
    }).then((data) => {
        if (data.updateBuildingModel.error) throw new Error(data.updateBuildingModel.error);
        return data.updateBuildingModel;
    });

const mutation = graphql(`
    mutation updateBuildingModel(
        $buildingModelUid: Uuid!
        $buildingProperties: BuildingProperties!
        $carbonPolicyIds: [Int!]
    ) {
        updateBuildingModel(
            buildingModelUid: $buildingModelUid
            buildingProperties: $buildingProperties
            carbonPolicyIds: $carbonPolicyIds
        ) {
            error
            buildingModelRunUid
            buildingModelUid
        }
    }
`);